import { formatINR, formatDate } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";

export const pddDays = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

// Days since the lead first entered its current status (based on history)
const stageEnteredAt = (lead) => {
  const hist = (lead.history || []).filter((h) => h.status === lead.status && h.at).sort((a, b) => (a.at < b.at ? -1 : 1));
  if (hist.length) return hist[0].at;
  return lead.created_at;
};

export const BASE_COLS = {
  lead_no: { label: "Lead #", cell: (l) => <span className="font-mono text-xs font-semibold text-emerald-700">{l.lead_no || "—"}</span> },
  customer: { label: "Customer", cell: (l) => (<div><div className="font-medium text-slate-900">{l.customer_name}</div><div className="text-xs text-slate-400">{l.customer_phone}</div></div>) },
  phone: { label: "Mobile", cell: (l) => l.customer_phone || "—" },
  email: { label: "Email", cell: (l) => l.customer_email || "—" },
  product: { label: "Product", cell: (l) => l.loan_type },
  amount: { label: "Loan Amount", align: "right", cell: (l) => <span className="font-mono">{formatINR(l.loan_amount)}</span> },
  sanction_amount: { label: "Sanction", align: "right", cell: (l) => <span className="font-mono text-slate-700">{l.sanction_amount ? formatINR(l.sanction_amount) : "—"}</span> },
  disbursed_amount: { label: "Disbursed", align: "right", cell: (l) => <span className="font-mono text-emerald-700 font-semibold">{l.disbursed_amount ? formatINR(l.disbursed_amount) : "—"}</span> },
  billed_amount: { label: "Billed", align: "right", cell: (l) => <span className="font-mono text-indigo-700">{l.billed_amount ? formatINR(l.billed_amount) : "—"}</span> },
  pending_amount: { label: "Pending", align: "right", cell: (l) => l.sanction_amount ? <span className={`font-mono ${l.pending_amount < 0 ? "text-rose-600 font-semibold" : "text-amber-700"}`}>{formatINR(l.pending_amount)}</span> : <span className="text-slate-400">—</span> },
  lender: { label: "Lender", cell: (l) => l.lender_name || "—" },
  partner: { label: "DSA / Partner", cell: (l) => l.partner_name || <span className="text-amber-600">Unassigned</span> },
  sales_manager: { label: "Sales Manager", cell: (l) => l.sales_manager_name || <span className="text-slate-400">—</span> },
  stage: { label: "Stage", cell: (l) => <StatusBadge status={l.status} /> },
  age_in_stage: {
    label: "Age in Stage", align: "right",
    cell: (l) => {
      const n = pddDays(stageEnteredAt(l));
      if (n === null) return "—";
      return <span className={`font-mono ${n > 15 ? "text-rose-600 font-semibold" : n > 7 ? "text-amber-600 font-semibold" : "text-slate-700"}`}>{n}d</span>;
    },
  },
  created: { label: "Created", cell: (l) => <span className="text-xs text-slate-400">{formatDate(l.created_at)}</span> },
  address: { label: "Address", cell: (l) => l.address || "—" },
  tehsil: { label: "Tehsil", cell: (l) => l.tehsil || "—" },
  city: { label: "City", cell: (l) => l.city || "—" },
  pincode: { label: "Pincode", cell: (l) => l.pincode || "—" },
  pan: { label: "PAN", cell: (l) => l.pan || "—" },
  aadhaar: { label: "Aadhaar", cell: (l) => l.aadhaar || "—" },
  bank_application_no: { label: "Bank App No", cell: (l) => l.bank_application_no || "—" },
  disbursement_no: { label: "Disbursement No", cell: (l) => l.disbursement_no || "—" },
  pdd_age: {
    label: "PDD Age", align: "right",
    cell: (l) => {
      const n = l.disbursed_at ? pddDays(l.disbursed_at) : null;
      if (n === null) return "—";
      return <span className={`font-mono ${n > 30 ? "text-rose-600 font-semibold" : "text-slate-700"}`}>{n}d</span>;
    },
  },
};

export const buildCfLabels = (products) => {
  const m = {};
  (products || []).forEach((p) => (p.fields || []).forEach((f) => { m[`cf:${f.key}`] = f.label; }));
  return m;
};

export const colDef = (key, cfLabels = {}) => {
  if (key && key.startsWith("cf:")) {
    const fk = key.slice(3);
    return { label: cfLabels[key] || fk.replace(/_/g, " "), cell: (l) => l.custom_fields?.[fk] ?? "—" };
  }
  return BASE_COLS[key];
};
